import React, { useRef, useState } from 'react';

import { likePost, dislikePost, removeLikePost, removeDislikePost,
    likeComment, dislikeComment, removeLikeComment, removeDislikeComment } from '../../api/api';
import { createComment, goingEvent, removeGoingEvent, interestedEvent, removeInterestedEvent } from '../../api/api';
import { defaultPfpUrl } from "../utils";
import { ThumbsUpIcon, ThumbsDownIcon, CommentIcon, ShareIcon } from "../../widgets/icons";
import { MentionTextarea } from "../ui/mention-textarea";
import { createMentionFetcher, createMentionPrefill } from "../../lib/mentions";
import { cn } from "../../lib/utils";
import ShareModal from "./ShareModal";
import {
    buildOptimisticComment,
    isOptimisticCommentId,
} from "./commentOptimistic";

const BAR_ACTION_CLASS =
    "inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1 py-2 font-emphasis text-compact text-text-secondary hover:bg-highlight hover:text-text-primary";
const INLINE_ACTION_CLASS =
    "flex items-center gap-1 rounded px-1.5 py-1 text-compact text-text-muted hover:bg-highlight hover:text-text-primary sm:px-2";

function alertUser(message) {
    alert(message);
}

/**
 * Share action that opens the share-to-chat modal.
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @param {"post"|"event"} props.contentType
 * @param {object} props.content
 * @param {object} [props.comment]
 * @param {boolean} [props.showLabel=true]
 * @param {"inline"|"bar"} [props.variant="inline"]
 * @returns {JSX.Element}
 */
function ShareButton({ userId, user, contentType, content, comment, showLabel = true, variant = "inline" }) {
    const [open, setOpen] = useState(false);
    const isBar = variant === "bar";

    return (
        <>
            <button
                type="button"
                className={isBar ? BAR_ACTION_CLASS : INLINE_ACTION_CLASS}
                onClick={() => setOpen(true)}
                aria-label="Share"
            >
                <ShareIcon size={isBar ? 16 : 18} />
                {showLabel ? (
                    <span className={isBar ? undefined : "hidden sm:inline"}>Share</span>
                ) : (
                    <span>Share</span>
                )}
            </button>
            {open ? (
                <ShareModal
                    open={open}
                    onOpenChange={setOpen}
                    userId={userId}
                    user={user}
                    contentType={contentType}
                    content={content}
                    comment={comment}
                />
            ) : null}
        </>
    );
}

function isOrgAccount(user) {
    return user?.type === "organization";
}

/**
 * Insert an optimistic top-level comment, then confirm or roll back in the background.
 * @param {Object} params
 * @param {number} params.userId
 * @param {object} [params.user]
 * @param {number} params.postId
 * @param {string} params.content
 * @param {function} [params.onCommentCreated]
 * @returns {true}
 */
function submitOptimisticTopLevelComment({
    userId,
    user,
    postId,
    content,
    onCommentCreated,
}) {
    const optimistic = buildOptimisticComment({
        userId,
        user,
        postId,
        parentId: 0,
        content,
    });
    onCommentCreated?.({ comment: optimistic });
    (async () => {
        try {
            const resp = await createComment({
                post_id: postId,
                parent_id: 0,
                author_id: userId,
                author_is_org: isOrgAccount(user),
                content,
            });
            if (!resp || resp.success === false || !resp.data) {
                console.error("Failed to create comment:", resp?.error);
                onCommentCreated?.({ removeTempId: optimistic.id });
                return;
            }
            onCommentCreated?.({
                comment: resp.data,
                replaceTempId: optimistic.id,
            });
        } catch (error) {
            console.error("Error creating comment:", error);
            onCommentCreated?.({ removeTempId: optimistic.id });
        }
    })();
    return true;
}

/**
 * Like and dislike controls.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {object} props.item
 * @param {"post"|"event"|"comment"} props.type
 * @param {"inline"|"bar"} [props.variant]
 * @returns {JSX.Element}
 */
function LikeButtons({ userId, user, item, type, variant = "inline" }) {
    const [likeState, setLikeState] = useState(item.user_liked || false);
    const [dislikeState, setDislikeState] = useState(item.user_disliked || false);
    const isOrg = isOrgAccount(user);
    var likeFunc, removeLikeFunc, dislikeFunc, removeDislikeFunc;
    if (type === "post" || type === "event") {
        likeFunc = likePost;
        removeLikeFunc = removeLikePost;
        dislikeFunc = dislikePost;
        removeDislikeFunc = removeDislikePost;
    } else if (type === "comment") {
        likeFunc = likeComment;
        removeLikeFunc = removeLikeComment;
        dislikeFunc = dislikeComment;
        removeDislikeFunc = removeDislikeComment;
    } else {
        throw new Error("LikeButtons: bad type: " + type);
    }

    const handleLike = async () => {
        if (!userId) {
            alertUser("You must be logged in to like");
            return;
        }
        if (isOptimisticCommentId(item.id)) {
            return;
        }

        if (!likeState) {
            setLikeState(true);
            const res = await likeFunc(item.id, userId, isOrg);
            if (!res || res.success === false) {
                setLikeState(false);
            }
            if (dislikeState) {
                setDislikeState(false);
                await removeDislikeFunc(item.id, userId, isOrg);
            }
        } else {
            setLikeState(false);
            const res = await removeLikeFunc(item.id, userId, isOrg);
            if (!res || res.success === false) {
                setLikeState(true);
            }
        }
    };

    const handleDislike = async () => {
        if (!userId) {
            alertUser("You must be logged in to dislike");
            return;
        }
        if (isOptimisticCommentId(item.id)) {
            return;
        }

        if (!dislikeState) {
            setDislikeState(true);
            const res = await dislikeFunc(item.id, userId, isOrg);
            if (!res || res.success === false) {
                setDislikeState(false);
            }
            if (likeState) {
                setLikeState(false);
                await removeLikeFunc(item.id, userId, isOrg);
            }
        } else {
            setDislikeState(false);
            const res = await removeDislikeFunc(item.id, userId, isOrg);
            if (!res || res.success === false) {
                console.log("Failed to remove dislike");
                setDislikeState(true);
            }
        }
    };

    const isBar = variant === "bar";
    const likeCount = (item.like_count || 0) + (likeState && !item.user_liked ? 1 : 0);
    const dislikeCount = (item.dislike_count || 0) + (dislikeState && !item.user_disliked ? 1 : 0);
    const reactionsLocked = isOptimisticCommentId(item.id);

    const likeButton = (
        <button
            className={cn(
                isBar ? BAR_ACTION_CLASS : INLINE_ACTION_CLASS,
                likeState && (isBar
                    ? "bg-highlight text-accent"
                    : "border border-accent bg-secondary text-accent")
            )}
            onClick={handleLike}
            disabled={reactionsLocked}
            aria-label="Like"
        >
            <ThumbsUpIcon filled={likeState} size={isBar ? 16 : 18} className={likeState ? "text-accent" : undefined} />
            {isBar ? <span>Like</span> : null}
            <span>{likeCount}</span>
        </button>
    );
    const dislikeButton = (
        <button
            className={cn(
                isBar ? BAR_ACTION_CLASS : INLINE_ACTION_CLASS,
                dislikeState && (isBar
                    ? "bg-highlight text-text-primary"
                    : "border border-border bg-secondary text-text-primary")
            )}
            onClick={handleDislike}
            disabled={reactionsLocked}
            aria-label="Dislike"
        >
            <ThumbsDownIcon filled={dislikeState} size={isBar ? 16 : 18} className={dislikeState ? "text-text-primary" : undefined} />
            {isBar ? <span>Dislike</span> : null}
            <span>{dislikeCount}</span>
        </button>
    );

    if (isBar) {
        return (
            <>
                {likeButton}
                {dislikeButton}
            </>
        );
    }

    return (
        <div className="flex shrink-0 items-center gap-1">
            {likeButton}
            {dislikeButton}
        </div>
    );
}

function GoingButtons({ userId, user, event }) {
    const [goingState, setGoingState] = useState(event.user_going || false);
    const [interestedState, setInterestedState] = useState(event.user_interested || false);
    const isOrg = isOrgAccount(user);

    const handleGoing = async () => {
        if (!userId) {
            alertUser("You must be logged in to go to an event");
            return;
        }

        if (!goingState) {
            setGoingState(true);
            const res = await goingEvent(event.id, userId, isOrg);
            if (!res || res.success === false) {
                setGoingState(false);
            }
            if (interestedState) {
                setInterestedState(false);
                await removeInterestedEvent(event.id, userId, isOrg);
            }
        } else {
            setGoingState(false);
            const res = await removeGoingEvent(event.id, userId, isOrg);
            if (!res || res.success === false) {
                setGoingState(true);
            }
        }
    };

    const handleInterested = async () => {
        if (!userId) {
            alertUser("You must be logged in to be interested in an event");
            return;
        }

        if (!interestedState) {
            setInterestedState(true);
            const res = await interestedEvent(event.id, userId, isOrg);
            if (!res || res.success === false) {
                setInterestedState(false);
            }
            if (goingState) {
                setGoingState(false);
                await removeGoingEvent(event.id, userId, isOrg);
            }
        } else {
            setInterestedState(false);
            const res = await removeInterestedEvent(event.id, userId, isOrg);
            if (!res || res.success === false) {
                setInterestedState(true);
            }
        }
    };

    const goingCount =
        typeof event.going_count === "number"
            ? event.going_count + (goingState && !event.user_going ? 1 : 0)
            : null;
    const interestedCount =
        typeof event.interested_count === "number"
            ? event.interested_count + (interestedState && !event.user_interested ? 1 : 0)
            : null;

    return (
        <>
            <button
                className={cn(BAR_ACTION_CLASS, goingState && "bg-highlight text-accent")}
                onClick={handleGoing}
                aria-label="Going"
            >
                <span role="img" aria-label="going">✅</span>
                <span>Going{goingCount != null ? ` ${goingCount}` : ""}</span>
            </button>
            <button
                className={cn(BAR_ACTION_CLASS, interestedState && "bg-highlight text-accent")}
                onClick={handleInterested}
                aria-label="Interested"
            >
                <span role="img" aria-label="interested">⭐</span>
                <span>Interested{interestedCount != null ? ` ${interestedCount}` : ""}</span>
            </button>
        </>
    );
}

/**
 * Like / reply / share actions for a comment, plus the click-to-open composer.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {object} props.comment
 * @param {boolean} [props.canReply] - False past the max reply depth.
 * @param {string} [props.replyAvatarSize]
 * @param {(text: string) => Promise<void>} [props.onReply]
 * @param {"post"|"event"} [props.contentType]
 * @param {object} [props.content]
 * @returns {JSX.Element}
 */
function CommentActionBar({
    userId,
    user,
    comment,
    canReply = false,
    replyAvatarSize,
    onReply,
    contentType,
    content,
}) {
    const [replyOpen, setReplyOpen] = useState(false);

    const handleReplyClick = () => {
        if (!userId) {
            alertUser("You must be logged in to reply to a comment");
            return;
        }
        setReplyOpen(!replyOpen);
    };

    const handleReplySubmit = (replyText) => {
        if (!onReply) {
            return false;
        }
        onReply(replyText);
        return true;
    };

    return (
        <>
            <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1">
                <LikeButtons userId={userId} user={user} item={comment} type="comment" />
                {canReply && (
                    <button
                        className={INLINE_ACTION_CLASS}
                        onClick={handleReplyClick}
                        aria-label="Reply"
                    >
                    <CommentIcon size={16} />
                    <span>Reply</span>
                </button>
                )}
                {content && contentType && !isOptimisticCommentId(comment.id) ? (
                    <ShareButton
                        userId={userId}
                        user={user}
                        contentType={contentType}
                        content={content}
                        comment={comment}
                        showLabel={false}
                    />
                ) : null}
            </div>
            {replyOpen && (
                <CommentEntry
                    user={user}
                    userId={userId}
                    placeholder="Write a reply..."
                    avatarSize={replyAvatarSize}
                    prefillMention={
                        comment.author_id != null
                            ? {
                                  id: Number(comment.author_id),
                                  name:
                                      comment.author_name ||
                                      (comment.author_is_org ? "Organization" : "User"),
                                  is_org: Boolean(comment.author_is_org),
                              }
                            : null
                    }
                    onSubmit={handleReplySubmit}
                    onClose={() => setReplyOpen(false)}
                    className="mb-2"
                />
            )}
        </>
    );
}

/**
 * Shared comment / reply composer. Textarea grows with wrapped text up to 4 lines,
 * then scrolls. Enter submits; Shift+Enter inserts a newline.
 *
 * @param {Object} props
 * @param {Object} [props.user]
 * @param {number} [props.userId]
 * @param {string} [props.placeholder]
 * @param {string} [props.avatarSize]
 * @param {object} [props.prefillMention] - Mention inserted when the composer opens.
 * @param {(text: string) => boolean | Promise<boolean | void>} props.onSubmit
 * @param {() => void} [props.onClose]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function CommentEntry({
    user,
    userId,
    placeholder,
    avatarSize = "w-10 h-10",
    prefillMention = null,
    onSubmit,
    onClose,
    className = "mb-4",
}) {
    const [seeded] = useState(() =>
        prefillMention ? createMentionPrefill(prefillMention) : { text: "", mentions: [] }
    );
    const [value, setValue] = useState(seeded.text);
    const submittingRef = useRef(false);
    const mentionRef = useRef(null);
    const fetchCandidates = React.useMemo(
        () => createMentionFetcher(userId || user?.id),
        [userId, user?.id]
    );

    const handleSubmit = () => {
        const text = (mentionRef.current?.getSerializedValue?.() ?? value).trim();
        if (!text || submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        const result = onSubmit(text);
        if (result === false) {
            submittingRef.current = false;
            return;
        }
        setValue("");
        onClose?.();
    };

    const handleCancel = () => {
        setValue('');
        if (onClose) {
            onClose();
        }
    };

    return (
        <div className={`w-full min-w-0 ${className}`}>
            <div className="flex min-w-0 gap-3">
                <div className="flex-shrink-0">
                    <img
                        src={user?.pfp_url || defaultPfpUrl}
                        alt="Profile"
                        className={`${avatarSize} rounded-full object-cover`}
                    />
                </div>

                <div className="flex min-w-0 flex-1 flex-col">
                    <MentionTextarea
                        ref={mentionRef}
                        placeholder={placeholder}
                        className="bg-secondary"
                        value={value}
                        initialMentions={seeded.mentions}
                        onChange={(e) => setValue(e.target.value)}
                        onSubmitEnter={handleSubmit}
                        fetchCandidates={fetchCandidates}
                        excludeId={userId || user?.id}
                        autoFocus
                    />

                    <div className="mt-2 flex justify-end gap-2">
                        <button
                            type="button"
                            className="px-3 py-1 text-compact text-text-secondary hover:text-text-primary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        {value.trim() ? (
                            <button
                                type="button"
                                className="rounded border border-accent bg-secondary px-4 py-1 text-compact text-accent hover:bg-secondary"
                                onClick={handleSubmit}
                            >
                                Post
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PostActionBar({ userId, user, post, onLike, onDislike, onCommentCreated }) {
    const [commentOpen, setCommentOpen] = useState(false);

    const handleCommentClick = () => {
        if (!userId) {
            alertUser("You must be logged in to comment on a post");
            return;
        }
        setCommentOpen(!commentOpen);
    };

    const handleCommentSubmit = (content) =>
        submitOptimisticTopLevelComment({
            userId,
            user,
            postId: post.id,
            content,
            onCommentCreated,
        });

    return (
        <>
        <div className="mt-1 flex min-w-0 flex-wrap items-stretch">
            <LikeButtons userId={userId} user={user} item={post} type="post" variant="bar" />
            <button
                className={BAR_ACTION_CLASS}
                onClick={handleCommentClick}
                aria-label="Comment"
                >
                <CommentIcon size={16} />
                <span>Comment</span>
            </button>
            <ShareButton userId={userId} user={user} contentType="post" content={post} variant="bar" />
        </div>
        {commentOpen && (
            <CommentEntry
                user={user}
                userId={userId}
                placeholder="Write a comment..."
                onSubmit={handleCommentSubmit}
                onClose={() => setCommentOpen(false)}
            />
        )}
        </>
    );
}

function EventActionBar({ userId, user, event, onCommentCreated }) {
    const [commentOpen, setCommentOpen] = useState(false);

    const handleCommentClick = () => {
        if (!userId) {
            alertUser("You must be logged in to comment on an event");
            return;
        }
        setCommentOpen(!commentOpen);
    };

    const handleCommentSubmit = (content) =>
        submitOptimisticTopLevelComment({
            userId,
            user,
            postId: event.id,
            content,
            onCommentCreated,
        });

    return (
        <>
            <div className="mt-1 flex min-w-0 flex-wrap items-stretch">
                <LikeButtons userId={userId} user={user} item={event} type="event" variant="bar" />
                <GoingButtons userId={userId} user={user} event={event} />
                <button
                    className={BAR_ACTION_CLASS}
                    onClick={handleCommentClick}
                    aria-label="Comment"
                >
                    <CommentIcon size={16} />
                    <span>Comment</span>
                </button>
                <ShareButton userId={userId} user={user} contentType="event" content={event} variant="bar" />
            </div>
            {commentOpen && (
                <CommentEntry
                    user={user}
                    userId={userId}
                    placeholder="Write a comment..."
                    onSubmit={handleCommentSubmit}
                    onClose={() => setCommentOpen(false)}
                />
            )}
        </>
    );
}

export { CommentActionBar, PostActionBar, EventActionBar };
